import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useApi, apiResolver } from "@/api/useApi";
import {
  SnippetResource,
  ArticleResource,
  UpdateSnippetAccessPayload,
  UpdateArticleAccessPayload,
} from "./ReleaseResourceAccess.model";

type ReleaseResourceAccessApi = {
  getSnippets: (releaseId: string) => Promise<SnippetResource[]>;
  getArticles: (releaseId: string, isInternal?: boolean) => Promise<ArticleResource[]>;
  updateSnippetAccess: (
    releaseId: string,
    simulatorId: string | undefined,
    payload: UpdateSnippetAccessPayload
  ) => Promise<void>;
  updateArticleAccess: (
    releaseId: string,
    simulatorId: string | undefined,
    payload: UpdateArticleAccessPayload
  ) => Promise<void>;
};

export function useReleaseResourceAccessApi(): ReleaseResourceAccessApi {
  const { api } = useApi();

  return useMemo(
    () => ({
      getSnippets: async (releaseId: string) =>
        apiResolver<SnippetResource[]>(
          api.get(`/knowledge-base-handler/release-resource-access/releases/${releaseId}/snippets`)
        ),
      getArticles: async (releaseId: string, isInternal?: boolean) => {
        const params = new URLSearchParams();
        if (isInternal !== undefined) {
          params.append("isInternal", String(isInternal));
        }
        const queryString = params.toString();
        return apiResolver<ArticleResource[]>(
          api.get(
            `/knowledge-base-handler/release-resource-access/releases/${releaseId}/articles${
              queryString ? `?${queryString}` : ""
            }`
          )
        );
      },
      updateSnippetAccess: async (
        releaseId: string,
        simulatorId: string | number | undefined,
        payload: UpdateSnippetAccessPayload
      ) =>
        apiResolver<void>(
          api.put(
            `/knowledge-base-handler/release-resource-access/releases/${releaseId}/snippets`,
            { ...payload, ...(simulatorId !== undefined && { simulatorId }) }
          )
        ),
      updateArticleAccess: async (
        releaseId: string,
        simulatorId: string | number | undefined,
        payload: UpdateArticleAccessPayload
      ) =>
        apiResolver<void>(
          api.put(
            `/knowledge-base-handler/release-resource-access/releases/${releaseId}/articles`,
            { ...payload, ...(simulatorId !== undefined && { simulatorId }) }
          )
        ),
    }),
    [api]
  );
}

// Query Keys
export const releaseResourceAccessKeys = {
  all: ["release-resource-access"] as const,
  snippets: (releaseId: string | number) =>
    [...releaseResourceAccessKeys.all, "snippets", String(releaseId)] as const,
  articles: (releaseId: string | number) =>
    [...releaseResourceAccessKeys.all, "articles", String(releaseId)] as const,
  wiki: (releaseId: string | number) =>
    [...releaseResourceAccessKeys.all, "wiki", String(releaseId)] as const,
};

// Query Hooks
export function useGetSnippetsByRelease(releaseId: string | number | undefined) {
  const api = useReleaseResourceAccessApi();

  return useQuery({
    queryKey: releaseResourceAccessKeys.snippets(releaseId ?? ""),
    queryFn: () => api.getSnippets(String(releaseId)),
    enabled: !!releaseId,
  });
}

export function useGetArticlesByRelease(releaseId: string | number | undefined) {
  const api = useReleaseResourceAccessApi();

  return useQuery({
    queryKey: releaseResourceAccessKeys.articles(releaseId ?? ""),
    queryFn: () => api.getArticles(String(releaseId), false),
    enabled: !!releaseId,
  });
}

export function useGetWikiByRelease(releaseId: string | number | undefined) {
  const api = useReleaseResourceAccessApi();

  return useQuery({
    queryKey: releaseResourceAccessKeys.wiki(releaseId ?? ""),
    queryFn: () => api.getArticles(String(releaseId), true),
    enabled: !!releaseId,
  });
}

// Mutation Hooks
export function useUpdateSnippetAccess(
  releaseId: string | number | undefined,
  simulatorId: string | number | undefined
) {
  const api = useReleaseResourceAccessApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateSnippetAccessPayload) =>
      api.updateSnippetAccess(
        String(releaseId),
        simulatorId === undefined ? undefined : String(simulatorId),
        payload
      ),
    onSuccess: () => {
      if (releaseId) {
        queryClient.invalidateQueries(releaseResourceAccessKeys.snippets(releaseId));
      }
    },
  });
}

export function useUpdateArticleAccess(
  releaseId: string | number | undefined,
  simulatorId: string | number | undefined
) {
  const api = useReleaseResourceAccessApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateArticleAccessPayload) =>
      api.updateArticleAccess(
        String(releaseId),
        simulatorId === undefined ? undefined : String(simulatorId),
        payload
      ),
    onSuccess: () => {
      if (releaseId) {
        queryClient.invalidateQueries(releaseResourceAccessKeys.articles(releaseId));
        queryClient.invalidateQueries(releaseResourceAccessKeys.wiki(releaseId));
      }
    },
  });
}
